<template>
    <div class="distance-measure-control">
        <button 
            class="btn btn-sm"
            :class="isMeasuring ? 'btn-danger' : 'btn-outline-primary'"
            @click="toggleMeasure"
            :title="isMeasuring ? 'Désactiver la mesure' : 'Activer la mesure de distance'"
        >
            <i class="fas fa-ruler"></i>
            {{ isMeasuring ? 'Arrêter la mesure' : 'Mesurer distance' }}
        </button>
        
        <!-- Bouton Annuler dernier point -->
        <button 
            v-if="measurePoints.length > 0"
            class="btn btn-sm btn-secondary ml-2"
            @click="undoLastPoint"
            title="Annuler le dernier point (Ctrl+Z)"
        >
            <i class="fas fa-undo"></i>
            Annuler
        </button>

        <!-- Bouton Effacer tout -->
        <button 
            v-if="measurePoints.length > 0"
            class="btn btn-sm btn-warning ml-2"
            @click="clearMeasures"
            title="Effacer toutes les mesures"
        >
            <i class="fas fa-trash"></i>
            Tout effacer
        </button>

        <!-- Affichage de la distance totale -->
        <div v-if="totalDistance > 0" class="distance-display mt-2">
            <strong>Distance totale :</strong> {{ formatDistance(totalDistance) }}
            <span v-if="measurePoints.length === 2" class="ml-2 text-muted">
                (entre 2 points)
            </span>
        </div>
    </div>
</template>

<style scoped>
.distance-measure-control {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.distance-display {
    padding: 8px 12px;
    background-color: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #dee2e6;
    font-size: 14px;
}

.ml-2 {
    margin-left: 8px;
}

.text-muted {
    color: #6c757d;
}
</style>

<script>
import { defineComponent } from 'vue';
import leaflet from 'leaflet';

export default defineComponent({
    name: 'DistanceMeasureTool',
    
    props: {
        map: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            isMeasuring: false,
            measurePoints: [],
            measureMarkers: [],
            measureLines: [],
            measureLabels: [],
            totalDistance: 0
        };
    },

    methods: {
        toggleMeasure() {
            this.isMeasuring = !this.isMeasuring;
            
            if (this.isMeasuring) {
                this.startMeasuring();
            } else {
                this.stopMeasuring();
            }

            // Émettre l'événement pour informer le parent
            this.$emit('measure-mode-changed', this.isMeasuring);
        },

        startMeasuring() {
            // Changer le curseur
            this.map.getContainer().style.cursor = 'crosshair';
            
            // Activer l'écoute des clics sur la carte
            this.map.on('click', this.onMapClick);
            
            // Afficher un message d'information
            this.showToast('Cliquez sur la carte ou sur des markers pour mesurer les distances');
        },

        stopMeasuring() {
            // Restaurer le curseur
            this.map.getContainer().style.cursor = '';
            
            // Désactiver l'écoute des clics
            this.map.off('click', this.onMapClick);
        },

        onMapClick(e) {
            const point = e.latlng;
            this.addMeasurePoint(point);
        },

        // Méthode appelée depuis le parent quand on clique sur un marker
        onMarkerClick(latlng, deviceInfo) {
            if (!this.isMeasuring) return;

            // Ajouter le point du marker
            this.addMeasurePoint(latlng, deviceInfo);
        },

        addMeasurePoint(latlng, deviceInfo = null) {
            this.measurePoints.push({ latlng, deviceInfo });
            
            // Créer un marqueur pour le point
            const markerIcon = leaflet.divIcon({
                className: 'measure-marker-icon',
                html: `<div style="
                    width: 14px;
                    height: 14px;
                    background-color: #FF5722;
                    border: 3px solid white;
                    border-radius: 50%;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                "></div>`,
                iconSize: [14, 14],
                iconAnchor: [7, 7]
            });

            const marker = leaflet.marker(latlng, { icon: markerIcon }).addTo(this.map);
            
            // Ajouter un popup si c'est un marker de device
            if (deviceInfo) {
                marker.bindPopup(`<strong>${deviceInfo.display_name || deviceInfo.name || 'Device'}</strong>`);
            }
            
            this.measureMarkers.push(marker);

            // Si on a au moins 2 points, tracer une ligne
            if (this.measurePoints.length > 1) {
                this.drawLine();
            }

            // Notification
            if (this.measurePoints.length === 1) {
                this.showToast('Point 1 placé. Cliquez pour placer le point 2');
            }
        },

        drawLine() {
            const lastIndex = this.measurePoints.length - 1;
            const point1 = this.measurePoints[lastIndex - 1].latlng;
            const point2 = this.measurePoints[lastIndex].latlng;
            
            // Tracer la ligne
            const line = leaflet.polyline([point1, point2], {
                color: '#FF5722',
                weight: 3,
                opacity: 0.8,
                dashArray: '5, 10'
            }).addTo(this.map);
            
            this.measureLines.push(line);
            
            // Calculer la distance
            const distance = this.map.distance(point1, point2);
            this.totalDistance += distance;
            
            // Afficher la distance au milieu du segment
            const midPoint = leaflet.latLng(
                (point1.lat + point2.lat) / 2,
                (point1.lng + point2.lng) / 2
            );
            
            const label = leaflet.marker(midPoint, {
                icon: leaflet.divIcon({
                    className: 'distance-label',
                    html: `<div style="
                        background-color: white;
                        padding: 6px 10px;
                        border-radius: 6px;
                        border: 2px solid #FF5722;
                        font-size: 13px;
                        font-weight: bold;
                        white-space: nowrap;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.25);
                    ">${this.formatDistance(distance)}</div>`,
                    iconSize: [0, 0]
                })
            }).addTo(this.map);
            
            this.measureLabels.push(label);
        },

        clearMeasures() {
            // Supprimer tous les marqueurs
            this.measureMarkers.forEach(marker => marker.remove());
            this.measureMarkers = [];
            
            // Supprimer toutes les lignes
            this.measureLines.forEach(line => line.remove());
            this.measureLines = [];
            
            // Supprimer tous les labels
            this.measureLabels.forEach(label => label.remove());
            this.measureLabels = [];
            
            // Réinitialiser les données
            this.measurePoints = [];
            this.totalDistance = 0;
            
            this.showToast('✨ Toutes les mesures effacées');
        },

        undoLastPoint() {
            if (this.measurePoints.length === 0) return;

            // Retirer le dernier point
            this.measurePoints.pop();

            // Retirer le dernier marker
            const lastMarker = this.measureMarkers.pop();
            if (lastMarker) lastMarker.remove();

            // Si on avait une ligne, la retirer aussi
            if (this.measureLines.length > 0) {
                const lastLine = this.measureLines.pop();
                if (lastLine) lastLine.remove();

                // Retirer le label de distance associé
                const lastLabel = this.measureLabels.pop();
                if (lastLabel) lastLabel.remove();

                // Recalculer la distance totale
                this.recalculateTotalDistance();
            }

            // Notification
            if (this.measurePoints.length === 0) {
                this.showToast('↩️ Tous les points annulés');
            } else {
                this.showToast(`↩️ Dernier point annulé (${this.measurePoints.length} point(s) restant(s))`);
            }
        },

        recalculateTotalDistance() {
            this.totalDistance = 0;
            
            for (let i = 1; i < this.measurePoints.length; i++) {
                const point1 = this.measurePoints[i - 1].latlng;
                const point2 = this.measurePoints[i].latlng;
                const distance = this.map.distance(point1, point2);
                this.totalDistance += distance;
            }
        },

        handleKeyDown(event) {
            // Ctrl+Z ou Cmd+Z pour annuler le dernier point
            if ((event.ctrlKey || event.metaKey) && event.key === 'z' && this.isMeasuring) {
                event.preventDefault();
                this.undoLastPoint();
            }
        },

        formatDistance(meters) {
            if (meters < 1000) {
                return `${Math.round(meters)} m`;
            } else {
                return `${(meters / 1000).toFixed(2)} km`;
            }
        },

        showToast(message) {
            const toast = document.createElement('div');
            toast.textContent = message;
            toast.style.position = 'fixed';
            toast.style.bottom = '30px';
            toast.style.left = '50%';
            toast.style.transform = 'translateX(-50%)';
            toast.style.padding = '12px 18px';
            toast.style.background = 'linear-gradient(135deg, #2196F3, #1976D2)';
            toast.style.color = 'white';
            toast.style.borderRadius = '10px';
            toast.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
            toast.style.fontFamily = 'system-ui, sans-serif';
            toast.style.fontSize = '14px';
            toast.style.zIndex = '9999';
            toast.style.opacity = '0';
            toast.style.transition = 'opacity 0.3s ease';

            document.body.appendChild(toast);

            setTimeout(() => {
                toast.style.opacity = '1';
            }, 50);

            setTimeout(() => {
                toast.style.opacity = '0';
                setTimeout(() => toast.remove(), 300);
            }, 3000);
        }
    },

    beforeUnmount() {
        // Nettoyer lors de la destruction du composant
        this.stopMeasuring();
        this.clearMeasures();
    }
});
</script>